import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Api } from '../utils/api';

const api = new Api();

export function EventWebpage() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [classes, setClasses] = useState([]);
    const [participants, setParticipants] = useState({});
    const [draws, setDraws] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEventData = async () => {
            try {
                setLoading(true);

                // Fetch event details
                const eventData = await api.getEvent(eventId);
                setEvent(eventData);

                // Fetch classes for this event
                const classesData = await api.getClasses(eventId);
                setClasses(classesData);

                // Fetch participants and draws for each class
                const participantsMap = {};
                const drawsMap = {};
                for (const classItem of classesData) {
                    try {
                        const classParticipants = await api.getParticipants(classItem.id);
                        participantsMap[classItem.id] = classParticipants;

                        // Try to fetch draw data
                        try {
                            const drawData = await api.getClassDraw(classItem.id);
                            if (drawData.draw && drawData.draw.length > 0) {
                                drawsMap[classItem.id] = drawData;
                            }
                        } catch (drawErr) {
                            // Draw doesn't exist yet, that's okay
                            console.log(`No draw for class ${classItem.id}:`, drawErr.message);
                        }
                    } catch (err) {
                        console.error(`Failed to fetch participants for class ${classItem.id}:`, err);
                        participantsMap[classItem.id] = [];
                    }
                }
                setParticipants(participantsMap);
                setDraws(drawsMap);

            } catch (err) {
                console.error('Error fetching event data:', err);
                setError('Failed to load event data');
            } finally {
                setLoading(false);
            }
        };

        if (eventId) {
            fetchEventData();
        }
    }, [eventId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'TBD';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'TBD';
        const time = new Date(`2000-01-01T${timeString}`);
        return time.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const getParticipantCount = (classId) => {
        return participants[classId]?.length || 0;
    };

    const getParticipantsForClass = (classId) => {
        return participants[classId] || [];
    };

    const getDrawForClass = (classId) => {
        return draws[classId] || null;
    };

    const getParticipantDrawNumber = (classId, participantId) => {
        const drawData = getDrawForClass(classId);
        if (!drawData || !drawData.draw) return null;

        const drawEntry = drawData.draw.find(p => p.participantId === participantId);
        return drawEntry ? drawEntry.drawNumber : null;
    };

    const getTotalParticipants = () => {
        return Object.values(participants).reduce((total, classParticipants) => total + classParticipants.length, 0);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading event details...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="text-destructive text-lg mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold mb-2">Event Not Found</h2>
                    <p className="text-muted-foreground mb-4">{error || 'The requested event could not be found.'}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b bg-card">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/')}
                                className="p-2 rounded-md hover:bg-accent transition-colors"
                            >
                                ← Back
                            </button>
                            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                                <span className="text-primary-foreground font-bold text-sm">🏇</span>
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Event Details</h1>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => window.print()}
                                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                            >
                                Print
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-6xl mx-auto space-y-8">
                    {/* Event Header */}
                    <div className="bg-card rounded-lg border p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-bold text-foreground mb-2">{event.name}</h1>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">📅</span>
                                        <span>{formatDate(event.date)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">📍</span>
                                        <span>{event.location || 'Location TBD'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">⏰</span>
                                        <span>{formatTime(event.startTime) || 'Time TBD'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-primary">{classes.length}</div>
                                <div className="text-sm text-muted-foreground">Classes</div>
                            </div>
                        </div>
                    </div>

                    {/* Classes and Participants */}
                    <div className="space-y-6">
                        {classes.map((classItem, index) => (
                            <div key={classItem.id} className="bg-card rounded-lg border overflow-hidden">
                                {/* Class Header */}
                                <div className="bg-muted/50 px-6 py-4 border-b">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <div>
                                            <h2 className="text-xl font-semibold text-foreground">
                                                {index + 1}. {classItem.name}
                                            </h2>
                                            <p className="text-sm text-muted-foreground">
                                                {classItem.description || 'No description available'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4 text-sm">
                                            <div className="text-center">
                                                <div className="font-semibold text-primary">{getParticipantCount(classItem.id)}</div>
                                                <div className="text-muted-foreground">Riders</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="font-semibold text-primary">${classItem.entryFee || 0}</div>
                                                <div className="text-muted-foreground">Entry Fee</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Participants List */}
                                <div className="p-6">
                                    {getParticipantsForClass(classItem.id).length > 0 ? (
                                        <div className="space-y-3">
                                            {getParticipantsForClass(classItem.id).map((participant, pIndex) => {
                                                const drawNumber = getParticipantDrawNumber(classItem.id, participant.id);
                                                const displayNumber = drawNumber || participant.runNumber || pIndex + 1;
                                                const hasDraw = !!drawNumber;

                                                return (
                                                    <div
                                                        key={participant.id}
                                                        className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border"
                                                    >
                                                        <div className="flex items-center gap-4">
                                                            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${hasDraw
                                                                ? 'bg-orange-500 text-white'
                                                                : 'bg-primary text-primary-foreground'
                                                                }`}>
                                                                {displayNumber}
                                                            </div>
                                                            <div>
                                                                <div className="font-medium text-foreground">
                                                                    {participant.name || participant.riderName}
                                                                </div>
                                                                <div className="text-sm text-muted-foreground">
                                                                    Horse: {participant.horseName || participant.horses?.[0] || 'Unknown'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            {participant.phone && (
                                                                <div className="text-sm text-muted-foreground">
                                                                    {participant.phone}
                                                                </div>
                                                            )}
                                                            {participant.homeTown && (
                                                                <div className="text-sm text-muted-foreground">
                                                                    {participant.homeTown}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <div className="text-2xl mb-2">🤔</div>
                                            <p>No participants registered yet</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Event Summary */}
                    <div className="bg-card rounded-lg border p-6">
                        <h2 className="text-xl font-semibold text-foreground mb-4">Event Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                <div className="text-2xl font-bold text-primary">{classes.length}</div>
                                <div className="text-sm text-muted-foreground">Total Classes</div>
                            </div>
                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                <div className="text-2xl font-bold text-primary">{getTotalParticipants()}</div>
                                <div className="text-sm text-muted-foreground">Total Participants</div>
                            </div>
                            <div className="text-center p-4 bg-muted/30 rounded-lg">
                                <div className="text-2xl font-bold text-primary">
                                    ${classes.reduce((total, c) => total + (c.entryFee || 0), 0)}
                                </div>
                                <div className="text-sm text-muted-foreground">Total Entry Fees</div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
} 